import { memo, useReducer, useRef } from 'react';
import { MediaPlaybackRateProvider } from '../context/MediaPlaybackRateContext';
import { MediaVolumeProvider } from '../context/MediaVolumeContext';
import { MediaPlaybackControlsProvider } from '../context/MediaPlaybackControlsContext';
import { MediaPlaybackTimeProvider } from '../context/MediaPlaybackTimeContext';
import { MediaElementProvider } from '../context/MediaElementContext';
import { mediaPlaybackReducer } from '../stores/AudioPlaybackStore';
import { useAnimateControls } from '../hooks/_internal/useAnimateControls';
import { UNKNOWN_SOURCE } from '../../types/MediaSourceTypes';
import { getInitialMediaPlaybackState } from '../utils/getInitialMediaPlaybackState';
import { MediaPlaybackBufferProvider } from '../context/MediaPlaybackBufferContext';
import { MediaTimelineCapabilitiesProvider } from '../context/MediaTimelineCapabilitiesContext';
import { jsx as _jsx } from "react/jsx-runtime";
const MediaPlayerProvider = /*#__PURE__*/memo(({
  audioDurationInSeconds,
  children,
  containInClipTime = false,
  engagementId,
  initialClipEndTime,
  initialClipStartTime,
  mediaType,
  recordingUrl,
  refetchMediaUrl,
  source,
  timelineCapabilities = {
    clipEditor: undefined
  }
}) => {
  const mediaProgressBarRef = useRef(null);
  const mediaRef = useRef(null);

  // TypeScript includes NaN as a valid number, so we need to check for it.
  const initialClipStartTimeProxy = !initialClipStartTime || isNaN(initialClipStartTime) ? 0 : initialClipStartTime;
  const [mediaState, mediaStateDispatch] = useReducer(mediaPlaybackReducer, getInitialMediaPlaybackState(audioDurationInSeconds, initialClipEndTime, initialClipStartTimeProxy));
  useAnimateControls({
    mediaProgressBarRef,
    mediaRef,
    mediaState
  });
  const sourceProxy = source || UNKNOWN_SOURCE;
  return /*#__PURE__*/_jsx(MediaPlaybackRateProvider, {
    mediaRef: mediaRef,
    children: /*#__PURE__*/_jsx(MediaVolumeProvider, {
      mediaRef: mediaRef,
      children: /*#__PURE__*/_jsx(MediaPlaybackControlsProvider, {
        audioDurationInSeconds: audioDurationInSeconds,
        clipEndTime: mediaState.clipEndTime,
        clipStartTime: mediaState.clipStartTime,
        containInClipTime: containInClipTime,
        errorType: mediaState.errorType,
        initialClipEndTime: initialClipEndTime,
        initialClipStartTime: initialClipStartTimeProxy,
        mediaRef: mediaRef,
        mediaStateDispatch: mediaStateDispatch,
        preciseDuration: mediaState.preciseDuration,
        refetchMediaUrl: refetchMediaUrl,
        roundedDuration: mediaState.roundedDuration,
        source: sourceProxy,
        status: mediaState.status,
        children: /*#__PURE__*/_jsx(MediaElementProvider, {
          bufferStatus: mediaState.bufferStatus,
          engagementId: engagementId,
          maybeRequiresAuthUpdate: mediaState.maybeRequiresAuthUpdate,
          mediaProgressBarRef: mediaProgressBarRef,
          mediaRef: mediaRef,
          mediaStateDispatch: mediaStateDispatch,
          mediaType: mediaType,
          recordingUrl: recordingUrl,
          refetchedMediaUrl: mediaState.refetchedMediaUrl,
          source: sourceProxy,
          children: /*#__PURE__*/_jsx(MediaPlaybackBufferProvider, {
            bufferStatus: mediaState.bufferStatus,
            percentLoaded: mediaState.percentLoaded,
            mediaStateDispatch: mediaStateDispatch,
            children: /*#__PURE__*/_jsx(MediaPlaybackTimeProvider, {
              value: mediaState.currentTime,
              children: /*#__PURE__*/_jsx(MediaTimelineCapabilitiesProvider, {
                value: timelineCapabilities,
                children: children
              })
            })
          })
        })
      })
    })
  });
});
export { MediaPlayerProvider };